import {
  AppBar,
  Button,
  buttonClasses,
  Checkbox,
  Grid,
  InputAdornment,
  InputBase,
  inputBaseClasses,
  Menu,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import { CloseCircle, DirectionDown, Magnifier } from 'asset';
import {
  DeleteDialog,
  FilterMenu,
  HeadTable,
  Layout,
  TableCaption,
} from 'components';
import DeleteAllDialog from 'components/common/DeleteAllDialog';
import { ButtonDelete, ButtonEdit } from 'components/common/table';
import PopupCreateOrEditPromos from 'components/Popups/PopupCreateOrEditPromos';
import { drawerWidth } from 'config';
import { getSortItem, hasPermission, NotifyService } from 'helpers';
import useDebounce from 'hooks/useDebounce';
import {
  DELETE_DIALOG_TYPE,
  FilterOption,
  FilterValue,
  GET_PROMOS_LIST_PARAMS,
  PROMOS_TYPE,
  promosStatusColor,
  TYPE_POPUP,
} from 'models';
import React, { useEffect, useMemo, useState } from 'react';
import { deleteManyPromosApi } from 'services/promotions';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setPromosListParams } from 'store/reducers/promotions';
import { getPromosListRequest } from 'store/reducers/promotions/actionType';
import { headCells } from './data';

const filterOptions: FilterOption[] = [];

const Promotions = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { promos_list } = useAppSelector((state) => state.promosReducer.promos);
  const { params } = promos_list;
  const [anchorElAction, setAnchorElAction] = useState<null | HTMLElement>(
    null
  );
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [keyword, setKeyword] = useState('');
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [openCreatePopup, setOpenCreatePopup] = useState(false);
  const [itemAction, setItemAction] = useState<PROMOS_TYPE>();
  const [openDeleteAll, setOpenDeleteAll] = useState<boolean>(false);

  const onSetListParams = (params: GET_PROMOS_LIST_PARAMS | null) => {
    dispatch(setPromosListParams(params));
  };

  useEffect(() => {
    if (params) {
      dispatch(getPromosListRequest(params));
    }
  }, [params, dispatch]);

  useEffect(() => {
    setKeyword(params?.search ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOpenCreate = () => {
    setOpenCreatePopup(true);
  };

  const setOpenDelete = (event: any, data: PROMOS_TYPE) => {
    event.stopPropagation();
    setItemAction(data);
    setOpenDeletePopup(true);
  };

  const setOpenEdit = (event: any, data: PROMOS_TYPE) => {
    event.stopPropagation();
    setItemAction(data);
    setOpenEditPopup(true);
  };

  const handleOpenBulkAction = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAction(event.currentTarget);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = promos_list.records.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (_: any, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: readonly string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const limit = parseInt(event.target.value, 10);
    onSetListParams({ ...params, page: 1, limit });
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const { items } = usePagination({
    count: promos_list.metadata.total_pages,
    page: promos_list.metadata.page,
    onChange(_event, page) {
      onSetListParams({ ...params, page: page });
    },
  });

  const onSetKeywordParams = useDebounce((keyword: string) => {
    const _params = { ...params, page: 1 };
    if (keyword) _params.search = keyword;
    else delete _params.search;
    onSetListParams(_params);
  }, 250);

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
    onSetKeywordParams(event.target.value);
  };

  const onClosePopup = (type: number) => {
    switch (type) {
      case TYPE_POPUP.DELETE:
        setOpenDeletePopup(false);
        setItemAction(undefined);
        break;
      case TYPE_POPUP.CREATE_OR_EDIT:
        setOpenEditPopup(false);
        setOpenCreatePopup(false);
        setItemAction(undefined);
        break;
      case TYPE_POPUP.DELETE_ALL:
        setOpenDeleteAll(false);
        break;
    }
  };

  const onReloadList = () => {
    if (!params) return;
    dispatch(getPromosListRequest(params));
  };
  const deleteManyPromocode = async () => {
    const promosIds = selected as string[];
    await deleteManyPromosApi(promosIds)
      .then((r) => {
        onReloadList();
        setSelected([]);
        onClosePopup(TYPE_POPUP.DELETE_ALL);
        NotifyService.success(r);
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {});
  };

  const filterValue: FilterValue = useMemo(() => {
    return {};
  }, []);

  const onChangeFilter = (value: FilterValue) => {
    setSelected([]);
    const _params = { ...params };

    onSetListParams({
      ..._params,
      ...value,
      page: 1,
    });
  };

  const sortItem = useMemo(() => {
    return getSortItem(promos_list.params?.order);
  }, [promos_list.params]);

  return (
    <Layout>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'white.light',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <Typography
              sx={{
                fontSize: '20px',
                lineHeight: 1,
                color: 'grey1.light',
              }}
            >
              Promotions
            </Typography>
          </Grid>
          <Grid display="flex" gap="8px" flexGrow={1} justifyContent="center">
            <InputBase
              placeholder="Search"
              sx={{
                maxWidth: '369px',
                flexGrow: 1,
                background: '#F2F4F6',
                border: '1px solid #D9D9D9',
                p: '0px 7px 0px 11px',
                borderRadius: '5px',
                [`& .${inputBaseClasses.input}`]: {
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '100%',
                  p: 0,
                  height: 'fit-content',
                },
              }}
              onChange={onChangeSearch}
              value={keyword}
              endAdornment={
                <InputAdornment position="end">
                  <Magnifier />
                </InputAdornment>
              }
            />
            <FilterMenu
              filterOptions={filterOptions}
              filterValue={filterValue}
              onChange={onChangeFilter}
            />
          </Grid>
          <Grid display="flex" gap="8px">
            {hasPermission(['superadmin', 'staff']) && (
              <>
                <Button
                  sx={{
                    background: '#fff',
                    border: `1px solid #226FE3`,
                    borderRadius: '3px',
                    boxShadow: 'none',

                    color: '#226FE3',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '100%',
                    textTransform: 'none',

                    p: '6px 16px',
                    height: '30px',
                    alignItems: 'flex-end',
                    ':hover': {
                      background: 'rgba(34, 111, 227, 0.08)',
                      border: `1px solid ${theme.palette.secondary.main}`,
                      borderRadius: '3px',
                      boxShadow: 'none',
                    },
                    [`& .${buttonClasses.endIcon}`]: {
                      mb: '-3px',
                      ml: '3px',
                    },
                  }}
                  variant="contained"
                  endIcon={<DirectionDown />}
                  onClick={handleOpenBulkAction}
                >
                  Bulk Action
                </Button>
                <Menu
                  sx={{
                    mt: '45px',
                    '& .MuiList-padding': {
                      pt: '0px !important',
                      pb: '0px !important',
                    },
                  }}
                  id="menu-appbar"
                  anchorEl={anchorElAction}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElAction)}
                  onClose={() => setAnchorElAction(null)}
                >
                  <Stack
                    sx={{
                      width: '319px',
                      p: '14px 22px 25px 23px',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      ':hover': {
                        backgroundColor: 'white.light',
                      },
                    }}
                  >
                    <Stack spacing="10px">
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '16px',
                          lineHeight: '100%',
                        }}
                      >
                        Bulk Actions
                      </Typography>
                      <Button
                        startIcon={
                          <CloseCircle
                            width={15}
                            heigh={15}
                            color={theme.palette.secondary.main}
                          />
                        }
                        variant="outlined"
                        color="secondary"
                        sx={{
                          p: 0,
                          fontWeight: 600,
                          fontSize: '15px',
                          lineHeight: 1,
                          width: '230px',
                          height: '32px',
                          borderRadius: '10px',
                          '& .MuiButton-startIcon': {
                            mr: '5px',
                          },
                        }}
                        onClick={() => setOpenDeleteAll(true)}
                      >
                        Delete Selected Promos
                      </Button>
                    </Stack>
                  </Stack>
                </Menu>
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{
                    height: 30,
                    width: '72px',
                    fontWeight: 600,
                    fontSize: '13px',
                    lineHeight: '100%',
                  }}
                  onClick={setOpenCreate}
                >
                  Create
                </Button>
              </>
            )}
          </Grid>
        </Toolbar>
      </AppBar>
      <Stack spacing="8px">
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #D9D9D9',
            maxHeight: 'calc(100vh - 150px)',
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 650 }}
            aria-labelledby="tableTitle"
          >
            <HeadTable<PROMOS_TYPE>
              numSelected={selected.length}
              order={sortItem?.order}
              orderBy={sortItem?.orderBy}
              onSelectAllClick={handleSelectAllClick}
              rowCount={promos_list.records.length}
              headCells={headCells}
            />
            <TableBody
              sx={{
                height: 'auto !important',
              }}
            >
              {promos_list.records.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                      }}
                    >
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onChange={(e) => handleClick(e, row.id)}
                        inputProps={{
                          'aria-labelledby': labelId,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row.aunty_promocode}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row.aunty_promo_title}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row.eber_hidden_code}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        textTransform: 'capitalize',
                        color: promosStatusColor[row.status || ''] || '#B6B6B6',
                      }}
                    >
                      {row.status}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      <Stack direction="row" spacing="10px">
                        {hasPermission(['superadmin', 'staff']) && (
                          <>
                            <ButtonEdit onClick={(e) => setOpenEdit(e, row)} />
                            <ButtonDelete
                              onClick={(e) => setOpenDelete(e, row)}
                            />
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableCaption
          rowsPerPage={params?.limit ?? 0}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          metadata={promos_list.metadata}
          items={items}
        />
      </Stack>

      {openDeletePopup && (
        <DeleteDialog
          name="Promocode"
          open={openDeletePopup}
          onClose={() => onClosePopup(TYPE_POPUP.DELETE)}
          subject={itemAction?.id}
          onSuccess={onReloadList}
          type={DELETE_DIALOG_TYPE.PROMO_CODES}
        />
      )}
      {openDeleteAll && (
        <DeleteAllDialog
          open={openDeleteAll}
          onClose={() => setOpenDeleteAll(false)}
          onClickDelete={deleteManyPromocode}
        />
      )}
      {((openEditPopup && itemAction) || openCreatePopup) && (
        <PopupCreateOrEditPromos
          open={true}
          subject={itemAction?.id}
          onSuccess={onReloadList}
          handleClosePopup={() => onClosePopup(TYPE_POPUP.CREATE_OR_EDIT)}
        />
      )}
    </Layout>
  );
};

export default Promotions;
